import { Ingredient } from '@cf-shared/data-model/ingredient';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';

/**
 * One change to a meal's ingredient list that a toast offers to take back (#6188): a past meal
 * put in (Add Again, or confirmed on the portion screen), a newly picked recipe split into its
 * foods, or a recipe row already in the meal split in place.
 */
export interface ListChange {
  /** A deep copy of the list just before the change. */
  before: Ingredient[];
  /** A deep copy of the list just after the change. */
  written: Ingredient[];
  /** The very objects that were put in. Found by identity first, then by value. */
  added: Ingredient[];
  /** Where the first of `added` sits in `written`. The rest follow it in order. */
  addedIndex: number;
  /** The row the added foods took the place of, when a row already in the meal was split. */
  replaced?: Ingredient;
}

export enum ListUndoOutcome {
  /** The list is back to what it was before the change. */
  Restored = 'Restored',
  /** Some of the added foods were no longer there; the ones that were are taken out. */
  Partly = 'Partly',
  /** Nothing could be taken back without risking food the user added since. */
  NotPossible = 'NotPossible',
}

/**
 * What Undo should do. `ingredients` is the list to show afterwards; `takenOut` holds the added
 * foods it takes out, so each can be counted as a removal.
 */
export type ListUndo =
  | { outcome: ListUndoOutcome.Restored; ingredients: Ingredient[]; takenOut: Ingredient[] }
  | { outcome: ListUndoOutcome.Partly; ingredients: Ingredient[]; takenOut: Ingredient[]; removed: number; expected: number }
  | { outcome: ListUndoOutcome.NotPossible };

/**
 * Work out what the list should be after Undo, given what it holds now.
 *
 * In this order:
 *
 * 1. **Nothing has changed since.** The list is put back exactly as it was before, amounts
 *    included. That is the only way to undo Split & Auto-Tune, which also re-weighs the rows the
 *    meal already held. Compared by VALUE, because the meal screen saves the list and hands it
 *    back as fresh objects, so identity does not survive.
 * 2. **The list moved on** (the user added, removed or reordered something). The added foods are
 *    taken out wherever they now sit, and anything else is left alone. A food is found by
 *    identity first; failing that, by value, taking the match nearest to where it was put, so an
 *    identical portion added at another time stays.
 *    - For an in-place split every part must still be there, and the recipe row goes back where
 *      the first part sits. With a part missing, putting the recipe back would count that part
 *      twice, so nothing is done.
 *    - For foods that were only added, whatever is still there is taken out, even when some are
 *      not: the user pressed Undo and every food that can still come out should.
 *
 * Pure: it neither reads nor changes anything it is given.
 */
export function undoListChange(change: ListChange, current: Ingredient[]): ListUndo {
  if (deepCompareEQ(current, change.written)) {
    return { outcome: ListUndoOutcome.Restored, ingredients: deepClone(change.before), takenOut: change.added };
  }

  const taken = new Set<number>();
  for (let i = 0; i < change.added.length; i++) {
    const index = findAddedIndex(current, change.added[i], change.addedIndex + i, taken);
    if (index >= 0) taken.add(index);
  }

  const takenOut = current.filter((_ingredient, index) => taken.has(index));
  const kept = current.filter((_ingredient, index) => !taken.has(index));

  if (change.replaced !== undefined) {
    if (taken.size < change.added.length) {
      return { outcome: ListUndoOutcome.NotPossible };
    }
    // every index before the first part is kept, so that is where the row goes back
    kept.splice(Math.min(...taken), 0, deepClone(change.replaced));
    return { outcome: ListUndoOutcome.Restored, ingredients: kept, takenOut };
  }

  if (taken.size === 0) {
    return { outcome: ListUndoOutcome.NotPossible };
  }
  if (taken.size < change.added.length) {
    return { outcome: ListUndoOutcome.Partly, ingredients: kept, takenOut, removed: taken.size, expected: change.added.length };
  }
  return { outcome: ListUndoOutcome.Restored, ingredients: kept, takenOut };
}

/** Where `added` sits in `current`, skipping places already matched; -1 when it is gone. */
function findAddedIndex(current: Ingredient[], added: Ingredient, expectedIndex: number, taken: Set<number>): number {
  const byIdentity = current.indexOf(added);
  if (byIdentity >= 0 && !taken.has(byIdentity)) return byIdentity;

  let best = -1;
  for (let i = 0; i < current.length; i++) {
    if (taken.has(i) || !deepCompareEQ(current[i], added)) continue;
    if (best < 0 || Math.abs(i - expectedIndex) < Math.abs(best - expectedIndex)) {
      best = i;
    }
  }
  return best;
}
